import React, { useState } from "react";
import { motion } from "framer-motion";

// ---------- HOBBIES ----------
const hobbies = [
  {
    title: "🏀 Basketball",
    description:
      "I played basketball at a national level and proudly represented the United Arab Emirates (UAE) in tournaments. Sports taught me discipline and teamwork, which I carry into my professional work.",
    image: "/img_7144.jpg",
    alt: "Basketball",
  },
  {
    title: "⚽ Other Sports",
    description:
      "I’ve also played soccer and table tennis competitively. I enjoy staying active and thrive in environments that combine strategy and physicality.",
  },
  {
    title: "🎮 Gaming",
    description:
      "Gaming is my way of relaxing and sparking creativity. I enjoy games like League of Legends, God of War, and games that blend storytelling and challenge.",
  },
  {
    title: "🧗 Rock Climbing",
    description:
      "I’ve always had a fear of heights... the kind that makes your knees wobble just standing on a third-floor balcony. But thanks to a few slightly reckless yet incredibly supportive friends, I found myself roped in (literally) to the world of rock climbing. It’s been a challenge, but one I’m embracing, and I hope to be as skilled as they are someday.",
    video: "/img_7611.mp4",
  },
  {
    title: "🎭 Stage Plays",
    description: (
      <>
        <p>
          Growing up, I had the opportunity to perform in several stage plays, which sparked my love for storytelling and performance. Here are a few highlights:
        </p>
        <ul style={{ paddingLeft: "1.2rem", margin: 0 }}>
          <li><strong>As You Like It</strong> – played Adam</li>
          <li><strong>Othello</strong> – played Desdemona’s father</li>
          <li><strong>The Adventures of Tom Sawyer</strong> – played Tom. Had a blast painting the fence on stage!</li>
        </ul>
      </>
    ),
  },
{
  title: "🎬 Movies",
  description: (
    <>
      <p>
        As a kid, I used to love watching movies - and I still do. I have my parents to thank (or blame) for that, since Friday nights at the movie theatre were a family tradition. Over time, it just became a thing. I enjoy a wide range of genres, especially comedies and romantic comedies. Jim Carrey is basically my spirit animal 🤣.
      </p>
      <p>
        I always find myself leaning toward rom-coms. Some of my go-to feel-good favorites include <em>When Harry Met Sally</em>, <em>Notting Hill</em>, and <em>Pride and Prejudice</em>.
      </p>
    </>
  ),
}
];


/* ---------- DEGREES ---------- */
const degrees = [
  {
    degree: "B.Tech Mechanical Engineering",
    minor : "Mechanical Design and Mechatronics",
    institution: "Manipal Academy of Higher Education, Dubai",
    year: "2015 - 2019",
    image: "/manipal_logo.png",
    alt: "Manipal Logo",
  },
  {
    degree: "M.Sc Mechanical Engineering",
    minor: "Dynamic Systems and Machine Learning",
    institution: "Florida Institute of Technology, USA",
    year: "2019 - 2021",
    image: "/FIT_logo.jpg",
    alt: "FIT Logo",
  },
];

const IntroHeader = () => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6 }}
    style={{ marginBottom: "1rem" }}
  />
);

const AboutDescription = () => {
  const [showMore, setShowMore] = useState(false);

  const shortText =
    "I am Arun Ramachandran, a dedicated software developer and automation engineer with a passion for building efficient, scalable solutions. With a strong foundation in test automation using Selenium and data analysis using Tableau, I bring a mix of technical proficiency and curiosity to every project.";

  const fullText = `I am Arun Ramachandran, a dedicated software developer and automation engineer with a passion for building efficient, scalable solutions. With a strong foundation in test automation using Selenium and data analysis using Tableau, I bring a mix of technical proficiency and curiosity to every project.

I thrive in fast-paced environments and enjoy solving problems through clean, maintainable code. My focus spans software development, automation, data science, and AI, and I’m always eager to learn and contribute to meaningful work.

Growing up in the Middle East and spending most of my life in Dubai has given me a unique perspective and adaptability that I bring into my professional life. This background fuels my drive to contribute meaningfully to projects that have real-world impact.`;

  return (
    <div style={{ marginTop: "0.7rem", lineHeight: "1.5", fontSize: "1rem" }}>
      <p style={{ whiteSpace: "pre-line" }}>{showMore ? fullText : shortText}</p>
      <button
        onClick={() => setShowMore(!showMore)}
        style={{
          marginTop: "0.5rem",
          background: "none",
          border: "none",
          color: "#007bff",
          cursor: "pointer",
          fontWeight: "600",
          padding: 0,
          fontSize: "0.9rem",
        }}
        aria-label={showMore ? "Show less description" : "Show more description"}
      >
        {showMore ? "Show less ▲" : "Show more ▼"}
      </button>
    </div>
  );
};

const AboutMe = () => (
  <div className="about-me-page" style={{ maxWidth: "900px", margin: "0 auto" }}>
    {/* === ABOUT SECTION === */}
    <section className="section-card" style={{ marginBottom: "3rem" }}>
      <h2 style={{ marginBottom: "0.4rem", fontSize: "1.8rem" }}>Hi 👋</h2>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "1.5rem",
          alignItems: "center",
        }}
      >
        <div style={{ width: "100%" }}>
          <IntroHeader />
          <AboutDescription />
        </div>
      </div>
    </section>

    {/* === EDUCATION SECTION === */}
    <section className="section-card" style={{ marginBottom: "3rem" }}>
      <h2 style={{ marginBottom: "1.5rem", fontSize: "1.8rem" }}>Education</h2>

      <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
        {degrees.map(({ degree, minor, institution, year, image, alt }, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.4, delay: idx * 0.2 }}
            style={{
              backgroundColor: "#fff",
              borderRadius: "10px",
              boxShadow: "0 3px 8px rgba(0,0,0,0.1)",
              padding: "1rem 1.3rem",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            {image && (
              <img
                src={image}
                alt={alt || institution}
                style={{
                  width: "250px",
                  height: "auto",
                  objectFit: "contain",
                  marginBottom: "1rem",
                }}
              />
            )}

            <h3 style={{ margin: 0, fontSize: "1.1rem" }}>{degree}</h3>

            {minor && (
              <p
                style={{
                  margin: "0.3rem 0 0",
                  fontSize: "0.9rem",
                  color: "#333",
                  fontStyle: "italic",
                }}
              >
                Minor: {minor}
              </p>
            )}

            <p
              style={{
                margin: "0.35rem 0 0",
                fontSize: "0.95rem",
                color: "#555",
              }}
            >
              {institution} {year && `: ${year}`}
            </p>
          </motion.div>
        ))}
      </div>
    </section>


    {/* === HOBBIES & INTERESTS === */}
    <section className="section-card">
      <h2 style={{ marginBottom: "1.5rem", fontSize: "1.8rem" }}>Hobbies & Interests</h2>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: "1.8rem",
        }}
      >
        {hobbies.map(({ title, description, image, alt, video }, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.5, delay: index * 0.25 }}
            style={{
              backgroundColor: "#fff",
              borderRadius: "12px",
              boxShadow: "0 3px 8px rgba(0,0,0,0.1)",
              padding: "1rem 1.3rem",
              userSelect: "none",
            }}
          >
            <h3 style={{ marginBottom: "0.6rem" }}>{title}</h3>
            <p style={{ fontSize: "0.95rem", lineHeight: "1.4", marginBottom: "0.8rem" }}>
              {typeof description === "string" ? description : null}
            </p>
            {typeof description !== "string" ? description : null}

            {image && (
              <img
                src={image}
                alt={alt || title}
                style={{
                  width: "100%",
                  borderRadius: "10px",
                  objectFit: "cover",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
                loading="lazy"
              />
            )}

            {video && (
              <video
                controls
                width="100%"
                style={{ borderRadius: "10px" }}
                preload="metadata"
              >
                <source src={video} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            )}
          </motion.div>
        ))}
      </div>
    </section>
  </div>
);

export default AboutMe;
